import { agentChip, type AgentTag } from "@boem/core/shared";
import { createContext, useContext, type ReactNode } from "react";

export function initials(n: string) {
  const parts = String(n).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** A person's colour: one of five token sets (the lane colours and a neutral, made for both themes), steady per name. */
export function toneOf(n: string): number {
  let h = 0;
  for (let i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) | 0;
  return Math.abs(h) % 5;
}

/** Someone's initials in their colour (the same on every card, note and pill). */
export function Avatar({ name, ai }: { name: string; ai?: boolean }) {
  return (
    <span className={`av${ai ? " av-ai" : ""}`} data-tone={toneOf(name)} title={ai ? `${name}'s AI app` : name}>
      {initials(name)}
    </span>
  );
}

/** "AI · Claude" beside a person's name: written by their AI app. */
export function AiChip({ agent }: { agent: AgentTag | null | undefined }) {
  if (!agent) return null;
  return (
    <span className={`ai-chip${agent.verified ? "" : " ai-unverified"}`} title={agent.how}>
      {agentChip(agent)}
      {agent.verified ? "" : "?"}
    </span>
  );
}

/** Item numbers that exist, plus how to jump to one - so "#12" in any text becomes a link. */
export const JumpContext = createContext<{
  nums: Set<number>;
  jump: (n: number) => void;
  /** how each item is numbered here ("01-07", "#188") */
  labels?: Map<number, string>;
  /** project numbers, current and left behind ("01-07"), to the item they belong to */
  keys?: Map<string, number>;
}>({
  nums: new Set(),
  jump: () => {},
});

/** Item references in text become links: #142, and project numbers like 01-07 (not dates). */
export function Linkify({ text }: { text: string }) {
  const { nums, jump, keys, labels } = useContext(JumpContext);
  const out: ReactNode[] = [];
  const re = /#(\d{1,4})(?!\d)|(?<![\d-])(\d{2,4}-\d{2,6})(?![\d-])/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const n = m[1] ? Number(m[1]) : keys?.get(normKey(m[2]!));
    if (n == null || !nums.has(n)) continue;
    out.push(text.slice(last, m.index));
    out.push(
      <button key={`${m.index}-${n}`} className="xref" type="button" title={`Jump to ${labels?.get(n) ?? `item ${n}`}`} onClick={() => jump(n)}>
        {m[0]}
      </button>,
    );
    last = m.index + m[0].length;
  }
  out.push(text.slice(last));
  return <>{out}</>;
}

/** "1-7" and "01-07" are the same project number. */
export const normKey = (k: string) => {
  const [p, s] = k.split("-");
  return `${String(Number(p)).padStart(2, "0")}-${String(Number(s)).padStart(2, "0")}`;
};

export const pad2 = (n: number) => String(n).padStart(2, "0");
